// Maker-checker for employee payout methods. Approvers (employee:change:approve)
// review pending bank / mobile-money submissions, approve or reject with a
// reason, and can expand a full audit trail per method. Mirrors the
// Change Approvals page so the two review surfaces feel identical.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Landmark, Loader2, Check, X, CalendarClock, User, Smartphone, Building2, History, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/features/shared/components/ui/dialog';
import { Textarea } from '@/features/shared/components/ui/textarea';
import toast from 'react-hot-toast';
import { RequirePermission } from '@/rbac/RequirePermission';
import { getAllEmployees, type PaymentMethod } from '@/lib/hrApi';
import {
  usePendingPaymentMethods, useApprovePaymentMethod, useRejectPaymentMethod, usePaymentMethodAudit,
} from '@/features/payment-methods/api/hooks';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { PendingSalarySplits } from '@/features/payment-methods/components/PendingSalarySplits';

// Same visual language as ChangeApprovalsPage's STATUS_BADGE map.
const STATUS_BADGE: Record<string, string> = {
  PENDING_VERIFICATION: 'bg-warning-soft text-warning-strong border-transparent',
  APPROVED: 'bg-success-soft text-success-strong border-transparent',
  REJECTED: 'bg-danger-soft text-danger-strong border-transparent',
  INACTIVE: 'bg-muted text-muted-foreground border-border',
};

const STATUS_LABEL: Record<string, string> = {
  PENDING_VERIFICATION: 'Pending Verification',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  INACTIVE: 'Inactive',
};

const fmtDateTime = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

/** Human label for the masked payout destination. */
export const describePaymentMethod = (m: PaymentMethod): string =>
  m.type === 'BANK'
    ? [m.bank_name, m.account_masked].filter(Boolean).join(' ') || 'Bank account'
    : [m.mobile_money_provider, m.phone_masked].filter(Boolean).join(' ') || 'Mobile money';

// ------------------------------------------------------------- audit expander
const AuditTrail: React.FC<{ employeeId: string; id: string }> = ({ employeeId, id }) => {
  const { data, isLoading } = usePaymentMethodAudit(employeeId, id);
  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…
      </div>
    );
  }
  const rows = data ?? [];
  if (rows.length === 0) {
    return <p className="mt-3 text-xs text-muted-foreground">No audit history recorded.</p>;
  }
  return (
    <ul className="mt-3 space-y-1.5 border-l-2 border-border pl-3">
      {rows.map((a) => (
        <li key={a.id} className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground capitalize">{a.action.replace(/_/g, ' ')}</span>
          {a.created_at ? <span> · {fmtDateTime(a.created_at)}</span> : null}
        </li>
      ))}
    </ul>
  );
};

// ------------------------------------------------------------------ one method
const PendingCard: React.FC<{
  method: PaymentMethod;
  nameOf: (id?: string | null) => string;
  onReject: (m: PaymentMethod) => void;
}> = ({ method, nameOf, onReject }) => {
  const approve = useApprovePaymentMethod();
  const [showAudit, setShowAudit] = useState(false);
  const TypeIcon = method.type === 'BANK' ? Building2 : Smartphone;

  const doApprove = async () => {
    try {
      await approve.mutateAsync(method.id);
      toast.success('Payout method approved.');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Could not approve this method.')));
    }
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground flex items-center gap-2">
              <User className="w-4 h-4 text-orange-500" /> {nameOf(method.employee_id)}
            </p>
            <p className="text-sm text-foreground mt-1.5 flex items-center gap-2">
              <TypeIcon className="w-4 h-4 text-muted-foreground" />
              {describePaymentMethod(method)}
              {method.account_holder_name ? (
                <span className="text-muted-foreground">· {method.account_holder_name}</span>
              ) : null}
            </p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
              <Badge variant="outline" className="text-[11px]">
                {method.type === 'BANK' ? 'Bank' : 'Mobile money'}
              </Badge>
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> {fmtDateTime(method.created_at)}
              </span>
              {method.branch ? <span>· {method.branch}</span> : null}
              {method.currency ? <span>· {method.currency}</span> : null}
            </p>
          </div>
          <Badge className={`border ${STATUS_BADGE[method.status] || STATUS_BADGE.INACTIVE}`}>
            {STATUS_LABEL[method.status] || method.status}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={doApprove} disabled={approve.isPending}>
            {approve.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
            Approve
          </Button>
          <Button
            variant="outline"
            className="text-danger border-red-200 hover:bg-red-50 dark:border-red-500/25"
            onClick={() => onReject(method)}
            disabled={approve.isPending}
          >
            <X className="w-4 h-4 mr-1" /> Reject
          </Button>
          <Button variant="ghost" className="text-muted-foreground ml-auto" onClick={() => setShowAudit((s) => !s)}>
            <History className="w-4 h-4 mr-1" /> History
            {showAudit ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        </div>

        {showAudit && <AuditTrail employeeId={method.employee_id} id={method.id} />}
      </CardContent>
    </Card>
  );
};

// ------------------------------------------------------------------- inner page
const PaymentMethodApprovalsInner: React.FC = () => {
  const { data, isLoading, isError, error } = usePendingPaymentMethods();
  const canApproveSplits = useHasPermission('payroll:approve');
  const reject = useRejectPaymentMethod();
  const [names, setNames] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<PaymentMethod | null>(null);
  const [reason, setReason] = useState('');

  // Directory lookup: employee UUID → display name (same pattern as ChangeApprovals).
  useEffect(() => {
    let alive = true;
    getAllEmployees()
      .then((emps) => {
        if (!alive) return;
        const map: Record<string, string> = {};
        emps.forEach((e) => {
          map[e.id] = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || e.id;
        });
        setNames(map);
      })
      .catch(() => { /* directory unavailable — fall back to raw ids */ });
    return () => { alive = false; };
  }, []);

  const nameOf = useMemo(
    () => (id?: string | null) => (id ? names[id] || id : '—'),
    [names],
  );

  const methods = data ?? [];

  const doReject = async () => {
    if (!rejecting || !reason.trim()) return;
    try {
      await reject.mutateAsync({ id: rejecting.id, reason: reason.trim() });
      toast.success('Payout method rejected.');
      setRejecting(null);
      setReason('');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Could not reject this method.')));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2">
          <Landmark className="w-6 h-6" /> Payment Method Approvals
        </h1>
        <p className="text-muted-foreground mt-1">
          Verify employee bank and mobile-money payout details before payroll can pay them.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading pending methods…
        </div>
      ) : isError ? (
        <Card>
          <CardContent role="alert" className="py-8 text-center text-danger-strong">
            {getApiErrorMessage(error, 'Could not load pending payout methods. Please refresh to try again.')}
          </CardContent>
        </Card>
      ) : methods.length === 0 ? (
        <Card className="border-orange-100 dark:border-orange-500/20">
          <CardContent className="py-12 text-center text-muted-foreground">
            No payout methods awaiting verification. You’re all caught up.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {methods.map((m) => (
            <PendingCard key={m.id} method={m} nameOf={nameOf} onReject={setRejecting} />
          ))}
        </div>
      )}

      {/* Salary splits (payroll owns the split; approvers decide it here too). */}
      {canApproveSplits && <PendingSalarySplits nameOf={nameOf} />}

      {/* Reject-with-reason dialog */}
      <Dialog open={!!rejecting} onOpenChange={(open) => { if (!open) { setRejecting(null); setReason(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject payout method</DialogTitle>
            <DialogDescription>
              {rejecting ? `Reject ${describePaymentMethod(rejecting)} for ${nameOf(rejecting.employee_id)}?` : ''}{' '}
              The employee will see your reason and can submit a corrected method.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection (required)"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(''); }} disabled={reject.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={doReject} disabled={reject.isPending || !reason.trim()}>
              {reject.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
              Reject method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PaymentMethodApprovalsPage: React.FC = () => (
  <RequirePermission perm="employee:payment:approve">
    <PaymentMethodApprovalsInner />
  </RequirePermission>
);

export default PaymentMethodApprovalsPage;
